import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { getUserSettings, upsertTargetScore } from "@/api/userSettings";
import { TARGET_SCORE_OPTIONS, type TargetScoreOption } from "@/lib/constant";
import { useAuthStore } from "@/store/authStore";

const userSettingsQueryKey = (userId: string) => ["userSettings", userId] as const;

const DEFAULT_TARGET_SCORE = TARGET_SCORE_OPTIONS[2]; // 98

/** 합격 기준 점수 조회 (DB 값, 없으면 기본값 98) */
export function useTargetScoreQuery() {
  const userId = useAuthStore(state => state.user?.id);

  return useQuery({
    queryKey: userSettingsQueryKey(userId!),
    queryFn: () => getUserSettings(userId!),
    enabled: Boolean(userId),
    select: data => (data?.target_score ?? DEFAULT_TARGET_SCORE) as TargetScoreOption,
  });
}

/** 합격 기준 점수 변경 뮤테이션 */
export function useUpdateTargetScoreMutation() {
  const userId = useAuthStore(state => state.user?.id);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (targetScore: TargetScoreOption) => upsertTargetScore(userId!, targetScore),
    onMutate: async (targetScore: TargetScoreOption) => {
      const key = userSettingsQueryKey(userId!);
      await queryClient.cancelQueries({ queryKey: key });

      const previous = queryClient.getQueryData(key);
      queryClient.setQueryData(key, { target_score: targetScore });

      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(userSettingsQueryKey(userId!), context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: userSettingsQueryKey(userId!) });
    },
  });
}
